import { DestroyRef, inject, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { filter } from 'rxjs';

export const EXECUTABLE_BY_ROUTE_PARAM = 'execute';

/**
 * Utility function that executes a callback when a specific URL parameter is present.
 * After execution, it automatically removes the parameter from the URL.
 *
 * @param fn - The callback function to execute when the route parameter is present
 * @param disabled - Signal that controls whether the callback should not be executed. Defaults to false.
 * @param executableByRouteParam - The name of the route parameter to check for. Defaults to EXECUTABLE_BY_ROUTE_PARAM.
 * @example
 * ```ts
 * // In component:
 * constructor() {
 *   injectExecutableByRoute(() => this.fn());
 * }
 *
 * // Or in Angular lifecycle hooks
 * constructor(private injector: Injector) {}
 *
 * ngOnInit() {
 *   runInInjectionContext(this.injector, () => {
 *     injectExecutableByRoute(() => this.someMethod());
 *   });
 * }
 * ```
 */
export const injectExecutableByRoute = (
  fn: (params: Record<string, string>) => void,
  disabled: Signal<boolean> = signal(false),
  executableByRouteParam: string = EXECUTABLE_BY_ROUTE_PARAM
) => {
  const route = inject(ActivatedRoute);
  const router = inject(Router);
  const destroyRef = inject(DestroyRef);

  if (!disabled()) {
    route.queryParams
      .pipe(
        takeUntilDestroyed(destroyRef),
        filter((params) => executableByRouteParam in params)
      )
      .subscribe((params) => {
        try {
          fn(params);
        } catch (error) {
          console.error(error);
        } finally {
          clearRouteParam(router, params, executableByRouteParam);
        }
      });
  } else {
    clearRouteParam(router, {}, executableByRouteParam);
  }

  return;
};

function clearRouteParam(router: Router, params: Record<string, string>, paramToClear: string) {
  const queryParams: Record<string, null | string> = { ...params };

  queryParams[paramToClear] = null;

  void router.navigate([], {
    queryParams,
    queryParamsHandling: 'merge',
  });
}
